import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Image from 'components/Basic/Image/Image';
import Pill from 'components/Basic/Pill/Pill';
import { useTopMenuArticles } from 'connectors/articleInterface/article/Article';
import { useNavigationItems } from 'connectors/navigation/Navigation';
import { useSettings } from 'connectors/Settings/Settings';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import Skeleton from 'react-loading-skeleton';
import { twJoin } from 'tailwind-merge';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

export type MobileMenuProps = {
    shouldStartAnimation: boolean;
    setIsMobileMenuOpen: (value: boolean) => void;
};

const itemTwClass =
    'relative min-w-[280px] py-3 pl-14 leading-6 text-primary tracking-wider no-underline border-b border-gray hover:no-underline hover:bg-grayLighter';

const NO_OF_HEADER_LINKS = 5;

const ESTIMATED_COUNT_OF_NAVIGATION_ITEMS = 15;

export const MobileMenu: FC<MobileMenuProps> = ({ shouldStartAnimation, setIsMobileMenuOpen }) => {
    const { navigationItems, fetching } = useNavigationItems();
    const t = useTypedTranslationFunction();
    const headerLinks = useTopMenuArticles(NO_OF_HEADER_LINKS);
    const domainConfig = useDomainConfig();
    const [storesUrl] = getInternationalizedStaticUrls(['/stores'], domainConfig.url);
    const settings = useSettings();

    if (navigationItems.length === 0 && !fetching) {
        return null;
    }

    return (
        <div className="relative w-full vl:hidden">
            <div
                className={twJoin(
                    'absolute top-0 right-0 z-menu flex w-[calc(100%-42px)] flex-col overflow-hidden bg-white shadow-mobileMenu transition-all',
                    shouldStartAnimation ? 'max-w-full' : 'max-w-0',
                )}
            >
                {!fetching
                    ? navigationItems.map((item, index) => (
                          <ExtendedNextLink
                              type={item.link ? 'static-presumed-category' : 'category'}
                              key={index}
                              href={item.link || item.slug}
                              className={itemTwClass}
                              onClick={() => {
                                  setIsMobileMenuOpen(false);
                              }}
                          >
                              <>
                                  <span className="color-primary absolute top-1/2 left-6 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center">
                                      <Image
                                          className="grayscale invert"
                                          src={item.image}
                                          width="20"
                                          height="20"
                                          alt={item.name}
                                      />
                                  </span>
                                  {item.name}
                              </>
                          </ExtendedNextLink>
                      ))
                    : Array(ESTIMATED_COUNT_OF_NAVIGATION_ITEMS)
                          .fill(null)
                          .map((_, index) => (
                              <div
                                  key={index}
                                  className="flex h-12 w-full items-center gap-3 border-b border-gray px-7"
                              >
                                  <Skeleton baseColor="" highlightColor="" className="h-6 w-6 !rounded-full" />
                                  <Skeleton
                                      className="mt-2 h-3 w-40"
                                      baseColor=""
                                      highlightColor=""
                                      style={{ width: (Math.random() + 1) * 85 }}
                                  />
                              </div>
                          ))}
                {settings && settings.storeCount > 0 && (
                    <ExtendedNextLink href={storesUrl} className={twJoin(itemTwClass, 'bg-grayLighter')} type="static">
                        <>
                            <Pill
                                className="absolute top-1/2 left-6 -translate-y-1/2 leading-none"
                                value={settings.storeCount}
                            />
                            {t('Stores')}
                        </>
                    </ExtendedNextLink>
                )}
                {headerLinks.map((link) => (
                    <ExtendedNextLink
                        href={link.link ?? link.slug}
                        className={twJoin(itemTwClass, 'bg-grayLighter')}
                        target={link.external ? '_blank' : '_self'}
                        key={link.name}
                        type="static"
                    >
                        {link.name}
                    </ExtendedNextLink>
                ))}
            </div>
        </div>
    );
};
